import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, PenLine, Loader2, FileDown, Archive } from "lucide-react";
import { jsPDF } from "jspdf";
import { streamPost, API } from "@/lib/stream";

const DOC_TYPES = [
  { value: "offer-letter", label: "Executive Offer Letter" },
  { value: "code-of-conduct", label: "Code of Conduct & AI Policy" },
  { value: "remote-work", label: "Remote Work Agreement" },
  { value: "pip", label: "Performance Improvement Plan" },
  { value: "job-description", label: "Job Description" },
];

const TONES = ["Warm & professional", "Crisp & corporate", "Friendly startup", "Formal legal"];

const MILO_AVATAR =
  "https://images.unsplash.com/photo-1740252117012-bb53ad05e370?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxjb2xvcmZ1bCUyMGF2YXRhciUyMGNoYXJhY3RlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDQ1MDE4Mnww&ixlib=rb-4.1.0&q=85&w=200";

export default function AiDocDrafter() {
  const [docType, setDocType] = useState("offer-letter");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [tone, setTone] = useState(TONES[0]);
  const [extras, setExtras] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [vaultState, setVaultState] = useState("idle");

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    setOutput("");
    setVaultState("idle");
    try {
      await streamPost(
        "/draft",
        { doc_type: docType, candidate_name: name, role, company, tone, extras },
        (delta) => setOutput((o) => o + delta)
      );
    } catch {
      setOutput("Drafting failed — please try again.");
    }
    setBusy(false);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFont("courier", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(output, 500);
    let y = 56;
    lines.forEach((ln) => {
      if (y > 740) {
        doc.addPage();
        y = 56;
      }
      doc.text(ln, 48, y);
      y += 14;
    });
    doc.save(`${docType}-draft.pdf`);
  };

  const saveVault = async () => {
    if (vaultState === "saving") return;
    setVaultState("saving");
    const label = DOC_TYPES.find((d) => d.value === docType)?.label || "HR Document";
    try {
      const res = await fetch(`${API}/vault`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `${label}${name ? ` — ${name}` : ""}`,
          content: output,
          doc_type: docType,
        }),
      });
      setVaultState(res.ok ? "saved" : "error");
    } catch {
      setVaultState("error");
    }
    if (vaultState !== "error") setTimeout(() => setVaultState("idle"), 3000);
  };

  const inputCls =
    "w-full bg-white rounded-2xl border-2 border-slate-900/10 px-4 py-3 text-sm font-medium outline-none focus:border-[#8B5CF6] transition-colors placeholder:text-slate-400";

  return (
    <section id="doc-drafter" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8B5CF6]">AI Tool №2 · Claude</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3">
            Milo drafts the paperwork. You keep the people.
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mt-4">
            Pick a document, set the tone, and watch Claude architect a complete, review-ready
            HR document in real time. Download it as a PDF or file it in the secure document vault —
            and always review with HR or legal before delivery.
          </p>
        </motion.div>

        <motion.div
          data-testid="claude-drafter-container"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 mt-12"
        >
          <div className="rounded-3xl border-2 border-slate-900/5 bg-white p-7 shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)] space-y-4">
            <div className="flex items-center gap-3 pb-2">
              <img src={MILO_AVATAR} alt="Milo, the Claude document architect" className="w-11 h-11 rounded-2xl object-cover border-2 border-slate-900/5" />
              <div>
                <div className="font-display font-bold text-sm">Milo · Document Architect</div>
                <div className="text-xs font-bold text-[#8B5CF6]">Claude Sonnet 4.6 engine</div>
              </div>
            </div>
            <select data-testid="claude-doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
            <input data-testid="claude-candidate-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Candidate / employee name" className={inputCls} />
            <input data-testid="claude-role-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role, e.g. Regional Community Manager" className={inputCls} />
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" className={inputCls} />
            <div className="flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTone(t)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold border-2 transition-colors duration-200 ${
                    tone === t ? "bg-[#8B5CF6] text-white border-[#8B5CF6]" : "border-slate-900/10 text-slate-600 hover:border-[#8B5CF6] hover:text-[#8B5CF6]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <textarea value={extras} onChange={(e) => setExtras(e.target.value)} placeholder="Perks, salary, special clauses…" rows={3} className={`${inputCls} resize-none`} />
            <button
              data-testid="claude-generate-button"
              onClick={generate}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#8B5CF6] text-white font-bold text-sm hover:bg-[#0F172A] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
              {busy ? "Claude is drafting…" : "Draft with Claude"}
            </button>
          </div>

          <div className="rounded-3xl bg-[#0F172A] p-7 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.4)] flex flex-col min-h-[28rem]">
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5722]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                <span className="ml-3 text-xs font-bold tracking-widest uppercase text-slate-400">Live draft preview</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="claude-copy-button"
                  onClick={copy}
                  disabled={!output}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 text-xs font-bold text-white hover:bg-[#FF5722] transition-colors disabled:opacity-40"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <button
                  data-testid="claude-download-pdf-button"
                  onClick={downloadPdf}
                  disabled={!output || busy}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#8B5CF6] text-xs font-bold text-white hover:bg-[#10B981] transition-colors disabled:opacity-40"
                >
                  <FileDown className="w-3.5 h-3.5" /> PDF
                </button>
                <button
                  data-testid="claude-save-vault-button"
                  onClick={saveVault}
                  disabled={!output || busy || vaultState === "saving"}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white transition-colors disabled:opacity-40 ${
                    vaultState === "saved" ? "bg-[#10B981]" : vaultState === "error" ? "bg-red-500" : "bg-white/10 hover:bg-[#F59E0B]"
                  }`}
                >
                  {vaultState === "saving" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : vaultState === "saved" ? <Check className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  {vaultState === "saved" ? "Vaulted" : vaultState === "error" ? "Retry" : "Vault"}
                </button>
              </div>
            </div>
            <div data-testid="claude-output-preview" className="doc-preview doc-scroll flex-1 overflow-y-auto pt-5 text-sm leading-relaxed text-slate-300">
              {output || (
                <span className="text-slate-500">
                  Your document will stream here, word by word, the moment Claude starts writing…
                </span>
              )}
              {busy && <span className="inline-block w-2 h-4 bg-[#8B5CF6] animate-pulse ml-0.5 align-middle" />}
            </div>
            {vaultState === "saved" && (
              <div data-testid="claude-vault-saved" className="pt-3 text-xs font-bold text-[#10B981]">Saved to the document vault — admins can download it from /admin.</div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
